import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  Card,
  IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';

const Dashboard = ({ searchTerm, setSearchSearchTerm }) => {
  const navigate = useNavigate();
  const [allNotes, setAllNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);

  const url = process.env.REACT_APP_API_URL || "https://codeforgesolution.com/SharedNotesApi/";
  const sUrl = "https://sharenotes.codeforgesolution.com/";

  useEffect(() => {
    const fetchAllNotes = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${url}get-all-notes`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setAllNotes(data.notes || data.data || []);
        } else {
          console.error('Failed to fetch notes:', response.status);
        }
      } catch (error) {
        console.error('Error fetching notes:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllNotes();
  }, [url]);

  const filteredNotes = allNotes.filter(note =>
    note.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    note.notes?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDeleteClick = (noteId) => {
    setNoteToDelete(noteId);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!noteToDelete) return;

    try {
      const response = await fetch(`${url}delete-note/${noteToDelete}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        setAllNotes(prevNotes => prevNotes.filter(note => note._id !== noteToDelete));
        setDeleteConfirmOpen(false);
        setNoteToDelete(null);
      } else {
        alert('Failed to delete note');
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('Error deleting note');
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setNoteToDelete(null);
  };

  if (loading && allNotes.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography>Loading notes...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', py: 4, px: 2 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Dashboard - All Notes
      </Typography>

      <Button
        variant="outlined"
        color="primary"
        sx={{ mt: 2, mb: 2 }}
        onClick={() => navigate('/')}
      >
        Home
      </Button>
      <TextField
        fullWidth
        placeholder="Search notes by title or content..."
        variant="outlined"
        value={searchTerm}
        onChange={(e) => setSearchSearchTerm(e.target.value)}
        sx={{ mb: 3 }}
      />

      {filteredNotes.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography>No notes found.</Typography>
          {!loading && (
            <Button variant="outlined" sx={{ mt: 2 }} onClick={() => setSearchSearchTerm('')}>
              Show all notes
            </Button>
          )}
        </Box>
      ) : (
        <Stack spacing={2}>
          {filteredNotes.map(note => (
            <Card
              key={note._id || note.id}
              sx={{
                p: 2,
                cursor: 'pointer',
                '&:hover': { shadow: 'lg' }
              }}
              onClick={() => window.location.href = `/${note._id || note.id}`}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    {note.title || 'Untitled'}
                  </Typography>
                  {note.notes && (
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary' }}
                    >
                      {note.notes.substring(0, 100)}{note.notes.length > 100 ? '...' : ''}
                    </Typography>
                  )}
                  {(note.createdAt || note.modified) && (
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary', mt: 1 }}
                    >
                      {new Date(note.createdAt || note.modified).toLocaleDateString()}
                    </Typography>
                  )}
                </Box>
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(note._id || note.id);
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Card>
          ))}
        </Stack>
      )}

      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
        aria-labelledby="confirm-dialog"
      >
        <DialogTitle id="confirm-dialog">Delete Note</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 1 }}>
            Are you sure you want to delete this note? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} autoFocus>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
